import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { captureError } from '@/lib/sentry';
import { GMAIL_CONFIG } from '@/lib/email/config';

export function EmailCallback() {
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const workspaceId = localStorage.getItem('connecting_workspace_id');
      const provider = localStorage.getItem('connecting_provider');

      try {
        if (!workspaceId) throw new Error('No workspace ID found');
        if (!provider) throw new Error('No provider type found');
        if (!code) throw new Error('No authorization code found');

        console.log('Starting token exchange...');
        // Exchange code for tokens
        const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            code,
            client_id: GMAIL_CONFIG.clientId,
            client_secret: GMAIL_CONFIG.clientSecret,
            redirect_uri: GMAIL_CONFIG.redirectUri,
            grant_type: 'authorization_code',
          }).toString(),
        });

        if (!tokenResponse.ok) {
          const errorData = await tokenResponse.json();
          console.error('Token exchange error:', errorData);
          throw new Error(errorData.error_description || 'Failed to exchange code');
        }

        const tokens = await tokenResponse.json();
        console.log('Token exchange successful');

        // Get user email using the access token
        const userResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
          headers: {
            'Authorization': `Bearer ${tokens.access_token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        });

        if (!userResponse.ok) {
          const errorData = await userResponse.json();
          console.error('User info error:', errorData);
          throw new Error('Failed to get user info');
        }

        const userData = await userResponse.json();
        console.log('User info retrieved successfully');

        // Get current user ID
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        // Calendar connect uses the same email callback URI — save as 'gmail' type
        const isCalendarConnect = provider === 'google_calendar';
        const dbType = isCalendarConnect ? 'gmail' : provider;

        // Step 1: upsert provider metadata WITHOUT tokens (clients cannot
        // write to the encrypted bytea columns).
        const { data: savedProvider, error: dbError } = await supabase
          .from('email_providers')
          .upsert({
            type: dbType,
            email: userData.email,
            workspace_id: workspaceId,
            user_id: user.id,
            expires_at: new Date(Date.now() + tokens.expires_in * 1000).toISOString(),
          }, {
            onConflict: 'workspace_id,type,email',
          })
          .select('id')
          .single();

        if (dbError) {
          console.error('Database error:', dbError);
          throw dbError;
        }

        // Step 2: store tokens via SECURITY DEFINER RPC (encrypts).
        const { error: tokError } = await supabase.rpc('email_provider_set_tokens', {
          p_provider_id:   savedProvider.id,
          p_access_token:  tokens.access_token,
          p_refresh_token: tokens.refresh_token,
          p_expires_at:    new Date(Date.now() + tokens.expires_in * 1000).toISOString(),
        });
        if (tokError) {
          console.error('email_provider_set_tokens failed:', tokError);
          throw tokError;
        }

        // If connecting for calendar, fetch calendar list from Google
        if (isCalendarConnect && savedProvider) {
          try {
            const calResponse = await fetch(
              'https://www.googleapis.com/calendar/v3/users/me/calendarList',
              { headers: { 'Authorization': `Bearer ${tokens.access_token}` } },
            );
            if (calResponse.ok) {
              const calData = await calResponse.json();
              const calendars = (calData.items || []).map((cal: any) => ({
                provider_id: savedProvider.id,
                workspace_id: workspaceId,
                user_id: user.id,
                external_id: cal.id,
                name: cal.summary || cal.id,
                color: cal.backgroundColor || null,
                is_primary: cal.primary || false,
                is_selected: cal.primary || false,
                access_role: cal.accessRole || 'reader',
              }));
              if (calendars.length > 0) {
                await supabase
                  .from('calendar_calendars')
                  .upsert(calendars, { onConflict: 'provider_id,external_id' });
              }
            }
          } catch (calError) {
            console.warn('Failed to fetch calendar list:', calError);
          }

          // Fire-and-forget: trigger sync to pull events immediately
          try {
            const { data: { session: currentSession } } = await supabase.auth.getSession();
            if (currentSession && savedProvider) {
              fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-sync`,
                {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${currentSession.access_token}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({ provider_id: savedProvider.id }),
                },
              );
            }
          } catch {
            // Sync will happen on calendar page load anyway
          }
        }

        // Clean up localStorage
        localStorage.removeItem('connecting_workspace_id');
        localStorage.removeItem('connecting_provider');

        toast({
          description: isCalendarConnect
            ? "Google Calendar connected successfully"
            : "Email account connected successfully",
        });

        // Redirect based on connect type
        navigate(isCalendarConnect ? '/settings/calendar' : '/settings/senders');
      } catch (error) {
        console.error('Error handling callback:', error);
        captureError(error as Error, { feature: 'gmail-oauth-callback' });
        setError(error instanceof Error ? error.message : 'An error occurred');
        toast({
          title: "Error",
          description: "Failed to connect email account",
          variant: "destructive",
        });
      }
    };

    handleCallback();
  }, [navigate, toast]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-semibold text-destructive">Connection Failed</h1>
          <p className="text-muted-foreground">{error}</p>
          <button
            onClick={() => navigate('/settings/senders')}
            className="text-primary hover:underline"
          >
            Return to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        <p className="text-muted-foreground">Connecting your email account...</p>
      </div>
    </div>
  );
}